import {jsx} from 'panel';

import {FeatureGate, ReportName} from 'common/report/constants';
import {ReportVisualization} from 'common/report/report-manager/visualization';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {getChartWidgetType, WidgetType, widgetTypeToTrackName} from 'common/report/funnels/util';
import {SortChangeEvent, Theme} from 'common/components/data-table/types';
import {ODAControlEl} from 'common/widgets/oda-control';
import {ODA_FUNNELS_UPSELL_DESCRIPTION} from 'common/widgets/oda-control/constants';
import {isGroupedEntry} from 'common/query-builder/query-entry-section/types';
import {isEventSegment} from 'common/query-builder/query-block/segment-menu/types';
import {emitTrackEvent} from 'common/util/dom';
import {ChartType, FunnelBookmark} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {TrackedEvents} from 'common/report/funnels/util/tracking';
import {createFunnelDataWithRename, hasData} from 'common/report/funnels/util';
import {SelectEl} from 'common/components/select';
import {StepRangeSelectorEl} from 'common/widgets/step-range-selector';
import {BucketSelectorEl} from 'common/widgets/bucket-selector';
import {
  bookmarkUpdateFromHeaderUpdate,
  chartHeaderAttrs,
  ChildHeaderUpdate,
  getAggregateTypeSelectorAttrs,
  getBucketSelectorAttrs,
  getSubChartSelectorAttrs,
  getTableColumnSelectorAttrs,
  getTrendCountTypeAttrs,
  resetTimeBuckets,
  selectAggregateType,
  selectChartView,
  selectTrendCountType,
  showAggregateSelector,
  showBucketSelector,
  showStepSelector,
  stepSelectorAttributes,
  updateSelectedColumn,
  updateStepSelection,
  updateTimeBuckets,
} from 'common/report/funnels/visualization/util';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {encode} from 'common/report/funnels/util/funnels-url';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {RequestOdaDetail} from 'common/report/report-manager/types';
import {SegmentFilterDataTableJsxProps} from 'common/charts/misc/segment-filter-data-table';
import {useFunnelsConsistency} from 'common/report/util/feature-sampling';

import {FunnelsChartDisplayEl} from './chart-display';

export class FunnelsVisualization extends ReportVisualization<FunnelBookmark, FunnelQueryResponse, TODO> {
  reportName = ReportName.Funnels;

  getFunnelQuery() {
    return FunnelsQuery.fromBookmark(this.bookmarkParams);
  }

  chartDisplay() {
    return (
      <FunnelsChartDisplayEl
        attrs={{
          elref: `chartDisplay`,
          'bookmark-name': `placeholder`,
          'resizing-layout': this.attr(`show-resizing-layout`),
          'rca-source-id': this.attr(`rca-source-id`),
        }}
        props={{
          bookmarkParams: this.bookmarkParams,
          queryResults: this.results,
          dataGroups: this.dataGroups,
        }}
        on={{
          update: ({detail}) => {
            const currFunnelQuery = this.getFunnelQuery();
            const updatedFunnelQuery = FunnelsQuery.createFromUpdate(currFunnelQuery, detail.delta);
            const requery = !currFunnelQuery.generatedQueryEquals(updatedFunnelQuery);

            this.dispatchUpdate({params: updatedFunnelQuery.generateBookmark(), requery});
          },
          track: (ev) => emitTrackEvent(this, ev.detail),
        }}
      />
    );
  }

  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    const widgetType = getChartWidgetType(this.bookmarkParams);
    const trackName = widgetTypeToTrackName[widgetType];

    return {
      'track-name': trackName,
      theme: widgetType === WidgetType.StepChart ? Theme.Funnels : Theme.Default,
    };
  }

  getBookmarkParamsFromSortChange(ev: SortChangeEvent): FunnelBookmark {
    const updatedFunnelQuery = FunnelsQuery.createFromUpdate(this.getFunnelQuery(), {
      sorting: ev.detail.sorting,
    });
    return updatedFunnelQuery.generateBookmark();
  }

  dataTableFooterTemplate() {
    const widgetType = getChartWidgetType(this.bookmarkParams);

    return (
      this.showOnDemandAnalysisFooter(widgetType) && (
        <ODAControlEl
          sel='.oda-control'
          attrs={{
            elref: `odaControl`,
            description: `Receive an email of statistically significant segments impacting this funnel.`,
            'show-upsell': !this.project?.getFeatureGate(FeatureGate.OnDemandAnalysis),
            'upsell-description': ODA_FUNNELS_UPSELL_DESCRIPTION,
          }}
          on={{
            clickAnalyze: () => {
              const query = new FunnelsQuery({
                ...this.getFunnelQuery(),
                groupBy: [],
              });
              const detail: RequestOdaDetail = {
                bookmarkDateRange: query.dateRange.getBookmarkFormat(),
                bookmarkUrl: encode({query}, {version: 2}),
              };
              this.dispatchEvent(
                new CustomEvent<RequestOdaDetail>(`request-oda`, {detail}),
              );
            },
          }}
        />
      )
    );
  }
  private showOnDemandAnalysisFooter(widgetType: WidgetType): boolean {
    if (![WidgetType.StepChart, WidgetType.TrendsChart].includes(widgetType)) {
      return false;
    }

    const {groupBy: groupByList, events} = FunnelsQuery.fromBookmark(this.bookmarkParams);
    const hasComparison = Array.isArray(events) && events.some(isGroupedEntry);
    const hasEventBreakdown = Array.isArray(groupByList) && groupByList.some(isEventSegment);

    return !this.attr(`is-download-mode`) && !hasComparison && !hasEventBreakdown;
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    return chartHeaderAttrs({
      funnelQuery: this.getFunnelQuery(),
      rawQueryResult: this.results,
    }) as any;
  }

  private handleChildHeaderUpdate({update, trackEvent}: ChildHeaderUpdate) {
    if (trackEvent) {
      emitTrackEvent(this, trackEvent.eventName, trackEvent.props);
    }

    const currFunnelQuery = this.getFunnelQuery();
    const updatedFunnelQuery = FunnelsQuery.createFromUpdate(currFunnelQuery, update);
    const requery = !currFunnelQuery.generatedQueryEquals(updatedFunnelQuery);

    this.dispatchUpdate({params: updatedFunnelQuery.generateBookmark(), requery});
  }

  protected headerChildrenTemplate() {
    const funnelQuery = this.getFunnelQuery();
    const chartType = funnelQuery.chartType;
    const hasChartData = this.results && hasData(createFunnelDataWithRename(this.results.data, funnelQuery));

    return (
      <div
        sel='.funnels-chart-header-controls'
        attrs={{
          slot: `chart-controls`,
        }}
      >
        {showStepSelector(funnelQuery) && (
          <StepRangeSelectorEl
            attrs={stepSelectorAttributes(funnelQuery)}
            on={{
              select: (ev) => this.handleChildHeaderUpdate(updateStepSelection(ev, this.getFunnelQuery())),
            }}
          />
        )}
        {showBucketSelector(funnelQuery, hasChartData) && (
          <BucketSelectorEl
            attrs={getBucketSelectorAttrs(funnelQuery)}
            on={{
              changeSelectedBucket: (ev) => this.handleChildHeaderUpdate(updateTimeBuckets(ev, this.getFunnelQuery())),
              resetBucket: (ev) => this.handleChildHeaderUpdate(resetTimeBuckets(ev, this.getFunnelQuery())),
            }}
          />
        )}
        {!useFunnelsConsistency() &&
          (chartType === ChartType.Trends || (chartType === ChartType.Frequency && hasChartData)) && (
            <SelectEl
              attrs={getSubChartSelectorAttrs(funnelQuery)}
              on={{
                change: (ev) => this.handleChildHeaderUpdate(selectChartView(ev, this.getFunnelQuery())),
              }}
            />
          )}
        {chartType === ChartType.Trends && (
          <SelectEl
            attrs={getTrendCountTypeAttrs(funnelQuery)}
            on={{
              change: (ev) => this.handleChildHeaderUpdate(selectTrendCountType(ev, this.getFunnelQuery())),
            }}
          />
        )}
        {!useFunnelsConsistency() && showAggregateSelector(funnelQuery) && (
          <SelectEl
            attrs={getAggregateTypeSelectorAttrs(funnelQuery)}
            on={{
              change: (ev) => this.handleChildHeaderUpdate(selectAggregateType(ev, this.getFunnelQuery())),
            }}
          />
        )}
        {chartType === ChartType.Chart && (
          <div sel='.funnel-table-column-selector'>
            <SelectEl
              attrs={getTableColumnSelectorAttrs(funnelQuery)}
              on={{
                change: (ev) => this.handleChildHeaderUpdate(updateSelectedColumn(ev, this.getFunnelQuery())),
              }}
            />
          </div>
        )}
      </div>
    );
  }

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail) {
    const previousFunnelQuery = this.getFunnelQuery();
    const queryUpdates = bookmarkUpdateFromHeaderUpdate(update, previousFunnelQuery);
    const updatedFunnelQuery = FunnelsQuery.createFromUpdate(previousFunnelQuery, queryUpdates);

    const hasChangedChartType = previousFunnelQuery.chartType !== updatedFunnelQuery.chartType;
    if (hasChangedChartType) {
      emitTrackEvent(this, {
        eventName: TrackedEvents.ChartSelected,
        props: {
          'Previous chart': previousFunnelQuery.chartType,
          'Selected chart': updatedFunnelQuery.chartType,
        },
      });
    }

    if (update.chartVizType) {
      let previousChartView = ``;
      let currentChartView = ``;

      switch (updatedFunnelQuery.chartType) {
        case ChartType.TimeToConvert:
          previousChartView = previousFunnelQuery.ttcVizType;
          currentChartView = updatedFunnelQuery.ttcVizType;
          break;
        case ChartType.Frequency:
          previousChartView = previousFunnelQuery.frequencyVizType;
          currentChartView = updatedFunnelQuery.frequencyVizType;
          break;
        case ChartType.Chart:
          previousChartView = previousFunnelQuery.stepsVizType;
          currentChartView = updatedFunnelQuery.stepsVizType;
          break;
      }
      emitTrackEvent(this, {
        eventName: TrackedEvents.ChartVisualizationSelected,
        props: {
          'Previous chart visualization': previousChartView,
          'New chart visualization': currentChartView,
        },
      });
    }

    return {
      params: updatedFunnelQuery.generateBookmark(),
      queryReason: QueryReason.QbOtherUpdate,
      requery: !previousFunnelQuery.generatedQueryEquals(updatedFunnelQuery),
      hasChangedChartType,
    };
  }
}

export const FunnelsVisualizationEl = defineAndMakeJsxFactory(`funnels-visualization`, FunnelsVisualization);
export const FunnelsVisualizationReact = panel2react(`funnels-visualization`, FunnelsVisualization);
